import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Repeat, ShoppingBag } from 'lucide-react'
import {
  Card,
  Chip,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  MoneyValue,
  SearchField,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import { getSaleStatusPresentation, paymentMethodLabel } from '@entities/sale'
import type { SalesView } from '@entities/sale'
import { SALE_STATUSES } from '@shared/contracts/enums'
import type { SaleStatus } from '@shared/contracts/enums'
import { useDebounce } from '@shared/lib/use-debounce'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { formatMoney } from '@shared/lib/format/currency'
import { useAdminSalesInfinite, exportAdminSales } from '../api/admin.api'
import type { ExportFormat, SaleItem } from '../api/admin.api'
import { downloadBlob, exportTimestamp } from '../lib/download-xlsx'
import { ExportButton } from './ExportButton'
import { adminChargeLabel, adminJourneyId, adminProductLabel } from '../lib/admin-sale-charge'
import { adminSaleStatusPresentation, saleFinancialImpact } from '../lib/admin-sale-events'
import { AdminSaleDetailDrawer } from './AdminSaleDetailDrawer'
import { AdminDashboardFilters } from './AdminDashboardFilters'
import type { DashboardSelection } from './AdminDashboardFilters'
import { adminListPeriodRange, dashboardRangeLabel } from '../lib/admin-dashboard-period'
import { TechnicalId } from './TechnicalId'
import { compactTechnicalId } from './technical-id'

import {
  alignRight,
  buyerCell,
  buyerName,
  buyerProduct,
  clickableRow,
  filterControls,
  funnelTag,
  header,
  hideOnMobile,
  mobileCard,
  mobileCardHeader,
  mobileCardLead,
  mobileList,
  mobileMetaGrid,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  page,
  productRow,
  resultCount,
  statusGroup,
  statusOption,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
  value,
  viewTabs,
} from './admin.css'

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const saleStatusOptions: ReadonlyArray<SaleStatus> = SALE_STATUSES

const viewOptions: ReadonlyArray<{ id: SalesView; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'new', label: 'Novas' },
  { id: 'renewals', label: 'Renovações' },
]

function DebouncedSearchInput({
  value,
  onCommit,
}: {
  value: string
  onCommit: (value: string) => void
}) {
  const [text, setText] = useState(value)
  const debounced = useDebounce(text, 300)

  useEffect(() => {
    onCommit(debounced.trim())
  }, [debounced, onCommit])

  return (
    <SearchField
      value={text}
      onChange={setText}
      placeholder="Buscar por comprador, e-mail, venda ou gateway"
      ariaLabel="Buscar vendas"
    />
  )
}

function filterSalesByView(items: SaleItem[], view: SalesView) {
  if (view === 'new') {
    return items.filter((item) => !item.isRenewal)
  }
  if (view === 'renewals') {
    return items.filter((item) => item.isRenewal)
  }
  return items
}

function saleBuyerLabel(item: SaleItem) {
  return item.buyerEmail ?? item.buyerName ?? 'Comprador'
}

function saleStatusTone(sale: SaleItem) {
  return adminSaleStatusPresentation(sale).tone
}

function AdminSaleAmount({ sale, size }: { sale: SaleItem; size?: 'sm' }) {
  const impact = saleFinancialImpact(sale)
  return (
    <span className={buyerCell}>
      <MoneyValue amount={impact.active.amount} currency={impact.active.currency} size={size} />
      {impact.hasImpact ? (
        <>
          <span className={buyerProduct}>
            de {formatMoney(impact.original.amount, impact.original.currency)} continuam válidos
          </span>
          <span className={buyerProduct}>
            {formatMoney(impact.affected.amount, impact.affected.currency)} foram reembolsados ou
            contestados
          </span>
        </>
      ) : null}
    </span>
  )
}

function saleStatusLabel(sale: SaleItem) {
  return adminSaleStatusPresentation(sale).label
}

function AdminSalesTable({
  items,
  onSelect,
}: {
  items: SaleItem[]
  onSelect: (saleId: string) => void
}) {
  return (
    <div className={tableViewport}>
      <table className={table}>
        <thead>
          <tr>
            <th className={th}>Comprador</th>
            <th className={`${th} ${hideOnMobile}`}>Data</th>
            <th className={th}>Status</th>
            <th className={`${th} ${hideOnMobile}`}>Gateway</th>
            <th className={`${th} ${alignRight}`}>Valor</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              className={clickableRow}
              key={item.id}
              role="button"
              tabIndex={0}
              aria-label={`Ver detalhe da cobrança de ${saleBuyerLabel(item)}: ${adminProductLabel(item)}`}
              onClick={() => onSelect(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(item.id)
                }
              }}
            >
              <td className={td}>
                <div className={buyerCell}>
                  <span className={buyerName}>{saleBuyerLabel(item)}</span>
                  <span className={productRow}>
                    <span className={buyerProduct} title={item.productId}>
                      {adminProductLabel(item)}
                    </span>
                    <span className={funnelTag}>{adminChargeLabel(item)}</span>
                    {adminJourneyId(item) !== item.id ? (
                      <span className={funnelTag} title={adminJourneyId(item)}>
                        Jornada {compactTechnicalId(adminJourneyId(item))}
                      </span>
                    ) : null}
                    {item.affiliateId ? <span className={funnelTag}>Afiliado</span> : null}
                    {item.coProducerId ? <span className={funnelTag}>Coprodutor</span> : null}
                    {item.globalMarket ? <span className={funnelTag}>Mercado Global</span> : null}
                    {item.isRenewal ? <span className={funnelTag}>Renovação</span> : null}
                    {item.coupon ? <span className={funnelTag}>Cupom</span> : null}
                  </span>
                  <TechnicalId label="ID" value={item.id} />
                </div>
              </td>
              <td className={`${td} ${hideOnMobile}`}>{formatDateTime(item.createdAt)}</td>
              <td className={td}>
                <StatusBadge tone={saleStatusTone(item)} label={saleStatusLabel(item)} size="sm" />
              </td>
              <td className={`${td} ${hideOnMobile}`}>
                <div className={buyerCell}>
                  <span className={value}>{item.gateway ?? 'n/a'}</span>
                  <span className={buyerProduct}>
                    {item.paymentMethod
                      ? paymentMethodLabel(item.paymentMethod)
                      : 'Método não informado'}
                  </span>
                </div>
              </td>
              <td className={`${td} ${alignRight}`}>
                <AdminSaleAmount sale={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminSalesCards({
  items,
  onSelect,
}: {
  items: SaleItem[]
  onSelect: (saleId: string) => void
}) {
  return (
    <div className={mobileList}>
      {items.map((item) => (
        <div
          className={mobileCard}
          key={item.id}
          role="button"
          tabIndex={0}
          aria-label={`Ver detalhe da cobrança de ${saleBuyerLabel(item)}: ${adminProductLabel(item)}`}
          onClick={() => onSelect(item.id)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onSelect(item.id)
            }
          }}
        >
          <div className={mobileCardHeader}>
            <span className={mobileCardLead}>
              <span className={buyerName}>{saleBuyerLabel(item)}</span>
              <span className={buyerProduct} title={item.productId}>
                {adminProductLabel(item)}
              </span>
              <TechnicalId label="ID" value={item.id} />
            </span>
            <StatusBadge tone={saleStatusTone(item)} label={saleStatusLabel(item)} size="sm" />
          </div>
          <div className={mobileMetaGrid}>
            <span className={mobileMetaItem}>
              <span className={mobileMetaLabel}>
                {saleFinancialImpact(item).hasImpact ? 'Continua válido' : 'Total'}
              </span>
              <span className={mobileMetaValue}>
                <AdminSaleAmount sale={item} size="sm" />
              </span>
            </span>
            <span className={mobileMetaItem}>
              <span className={mobileMetaLabel}>Data</span>
              <span className={mobileMetaValue}>{formatDateTime(item.createdAt)}</span>
            </span>
            <span className={mobileMetaItem}>
              <span className={mobileMetaLabel}>Pagamento</span>
              <span className={mobileMetaValue}>
                {item.paymentMethod ? paymentMethodLabel(item.paymentMethod) : 'Não informado'}
              </span>
              <span className={buyerProduct}>{item.gateway ?? 'gateway não informado'}</span>
            </span>
            <span className={mobileMetaItem}>
              <span className={mobileMetaLabel}>Origem</span>
              <span className={mobileMetaValue}>{adminChargeLabel(item)}</span>
              <span className={productRow}>
                {adminJourneyId(item) !== item.id ? (
                  <span className={funnelTag} title={adminJourneyId(item)}>
                    Jornada {compactTechnicalId(adminJourneyId(item))}
                  </span>
                ) : null}
                {item.affiliateId ? <span className={funnelTag}>Afiliado</span> : null}
                {item.coProducerId ? <span className={funnelTag}>Coprodutor</span> : null}
                {item.globalMarket ? <span className={funnelTag}>Global</span> : null}
                {item.coupon ? <span className={funnelTag}>Cupom</span> : null}
              </span>
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export function AdminSalesScreen() {
  const [q, setQ] = useState('')
  const [searchResetVersion, setSearchResetVersion] = useState(0)
  const preserveSearchFocus = useFeatureFlag('adminSalesSearchFocus')
  const [selection, setSelection] = useState<DashboardSelection>({ period: 'today' })
  const range = useMemo(
    () =>
      selection.period === 'custom'
        ? (selection.custom ?? {})
        : adminListPeriodRange(selection.period),
    [selection],
  )
  const [status, setStatus] = useState<SaleStatus | undefined>()
  const [view, setView] = useState<SalesView>('all')
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width: 720px)')

  const sales = useAdminSalesInfinite({
    includeFunnel: true,
    ...range,
    ...(q ? { q } : {}),
    ...(selection.user?.id ? { participantId: selection.user?.id } : {}),
    ...(status ? { status } : {}),
  })
  const items = useMemo(
    () => sales.data?.pages.flatMap((page) => page.items) ?? [],
    [sales.data?.pages],
  )
  const visibleItems = useMemo(() => filterSalesByView(items, view), [items, view])
  const filtersActive = Boolean(q) || Boolean(status) || Boolean(selection.user?.id)

  const commitSearch = useCallback(
    (value: string) => {
      if (value === q) return
      setQ(value)
      if (!preserveSearchFocus) setSearchResetVersion((version) => version + 1)
    },
    [preserveSearchFocus, q],
  )

  function clearSearch() {
    setQ('')
    if (preserveSearchFocus || q) setSearchResetVersion((version) => version + 1)
  }

  async function handleExport(format: ExportFormat) {
    const params = {
      includeFunnel: true,
      ...range,
      ...(q ? { q } : {}),
      ...(selection.user?.id ? { participantId: selection.user.id } : {}),
      ...(status ? { status } : {}),
    }
    const blob = await exportAdminSales(params, format)
    downloadBlob(blob, `cobracoes-${exportTimestamp()}.${format}`)
  }

  const chips: ReactNode[] = []
  if (status) {
    chips.push(
      <Chip
        key="status"
        label={getSaleStatusPresentation(status).label}
        onRemove={() => setStatus(undefined)}
      />,
    )
  }
  if (q) {
    chips.push(<Chip key="q" label={`Busca: ${q}`} onRemove={clearSearch} />)
  }
  if (selection.user?.id) {
    chips.push(
      <Chip
        key="user"
        label={`Usuário: ${selection.user?.name || selection.user?.email}`}
        onRemove={() => {
          setSelection({ ...selection, user: undefined })
        }}
      />,
    )
  }

  return (
    <section className={page} data-testid="sales-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Cobranças</h1>
          <p className={subtitle}>
            Visão operacional por cobrança, com vínculo à jornada comercial e filtro por usuário.
          </p>
        </div>
        <ExportButton onExport={handleExport} />
      </div>

      <AdminDashboardFilters selection={selection} onApply={setSelection} />
      <p className={subtitle}>
        Cobranças criadas: {dashboardRangeLabel(range)}.{' '}
        {selection.user
          ? `Usuário: ${selection.user.name || selection.user.email}`
          : 'Todos os usuários'}
        .
      </p>

      <Card padding="md" className={tableCard}>
        <div className={viewTabs} role="group" aria-label="Filtrar cobranças por tipo">
          {viewOptions.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={view === tab.id}
              className={statusOption({ active: view === tab.id })}
              onClick={() => setView(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <FilterToolbar
          data-testid="sales-filters"
          chips={chips.length > 0 ? chips : null}
          onClear={
            filtersActive
              ? () => {
                  clearSearch()
                  setStatus(undefined)
                  setSelection({ ...selection, user: undefined })
                }
              : undefined
          }
          resultCount={
            <span className={resultCount}>
              {visibleItems.length} {visibleItems.length === 1 ? 'resultado' : 'resultados'}
            </span>
          }
        >
          <div className={filterControls}>
            <DebouncedSearchInput key={searchResetVersion} value={q} onCommit={commitSearch} />

            <div className={statusGroup} role="group" aria-label="Filtrar por status">
              {saleStatusOptions.map((option) => {
                const presentation = getSaleStatusPresentation(option)
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={status === option}
                    className={statusOption({ active: status === option })}
                    onClick={() => setStatus(status === option ? undefined : option)}
                  >
                    {presentation.label}
                  </button>
                )
              })}
            </div>
          </div>
        </FilterToolbar>

        {sales.isPending ? (
          <Skeleton height={320} />
        ) : sales.isError ? (
          <ErrorState
            onRetry={() => {
              void sales.refetch()
            }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShoppingBag size={22} />}
            title="Nenhuma cobrança encontrada"
            description="Ajuste os filtros para consultar outros compradores, cobranças ou participantes."
          />
        ) : visibleItems.length === 0 ? (
          <EmptyState
            icon={view === 'renewals' ? <Repeat size={22} /> : <ShoppingBag size={22} />}
            title={view === 'renewals' ? 'Nenhuma renovação carregada' : 'Nenhuma cobrança inicial'}
            description="Use Carregar mais para continuar procurando nas próximas páginas."
          />
        ) : (
          <>
            {isMobile ? (
              <AdminSalesCards items={visibleItems} onSelect={setSelectedSaleId} />
            ) : (
              <AdminSalesTable items={visibleItems} onSelect={setSelectedSaleId} />
            )}
            <LoadMore
              shown={items.length}
              hasMore={sales.hasNextPage}
              loading={sales.isFetchingNextPage}
              onClick={() => {
                void sales.fetchNextPage()
              }}
            />
          </>
        )}
      </Card>

      <AdminSaleDetailDrawer saleId={selectedSaleId} onClose={() => setSelectedSaleId(null)} />
    </section>
  )
}
